import { c as _c } from "react/compiler-runtime";
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { ContentTrack, ScoContentTrackEnrollment, ScoContentTrackStatus, ContentTrackProgression } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useParams } from 'next/navigation';
import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';
type ContentTrackContextProps = {
  children?: ReactNode | ReactNode[];
};
type ContentTrackContextValue = {
  pathPrefix?: string;
  contentTrack?: ContentTrack;
  contentTrackSlug?: string;
  contentTrackSectionSlug?: string;
  contentTrackStepSlug?: string;
  contentTrackEnrollment?: ScoContentTrackEnrollment;
  progressionType?: ContentTrackProgression;
  getProgress?: (contentTrackSectionId?: string, contentTrackStepId?: string) => Partial<ScoContentTrackStatus>;
  children?: ReactNode;
};
export const ContentTrackContext = createContext<ContentTrackContextValue>({});
export const useContentTrackContext = () => {
  return useContext(ContentTrackContext);
};
export const ContentTrackContextProvider: FC<ContentTrackContextValue> = props => {
  const $ = _c(31);
  const routeParams = useParams();
  const {
    currentUser
  } = useCurrentUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($contentTrackSlug: String) {
        contentTrack(where: { slug: $contentTrackSlug }) {
          id
          title
          slug
          type
          progressionType
          sections {
            id
            slug
            steps {
              id
              slug
            }
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = routeParams?.contentTrackSlug;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      contentTrackSlug: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !routeParams?.contentTrackSlug;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      variables: t2,
      skip: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const trackQuery = useQuery(t0, t4);
  const track = trackQuery.data?.contentTrack;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = gql`
    query ($contentTrackId: String, $contentTrackSlug: String) {
      scoContentTrackEnrollment(contentTrackId: $contentTrackId, contentTrackSlug: $contentTrackSlug) {
        id
        contentTrackId
        contentTrackEnrollmentId
        contentTrack {
          id
          attachmentType
          attachmentId
          slug
          title
        }
        progress {
          ...StatusFragment
          sectionStatuses {
            ...StatusFragment
            stepStatuses {
              ...StatusFragment
            }
          }
        }
      }
    }
    fragment StatusFragment on ScoContentTrackStatus {
      id
      contentTrackStatusId
      completedAt
      completionAsPercent
      startedAt
      targetId
      targetType
    }
  `;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [runEnrollmentQuery, enrollmentQuery] = useLazyQuery(t5);
  const enrollment = enrollmentQuery.data?.scoContentTrackEnrollment;
  let t6;
  if ($[7] !== runEnrollmentQuery || $[8] !== track?.id || $[9] !== track?.slug) {
    t6 = () => {
      if (track?.id || track?.slug) {
        runEnrollmentQuery({
          variables: {
            contentTrackId: track?.id,
            contentTrackSlug: track?.slug
          }
        });
      }
    };
    $[7] = runEnrollmentQuery;
    $[8] = track?.id;
    $[9] = track?.slug;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = track?.id;
  const t8 = track?.slug;
  const t9 = currentUser?.userId;
  let t10;
  if ($[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = [t7, t8, t9];
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  useEffect(t6, t10);
  let t11;
  if ($[15] !== enrollment?.progress) {
    t11 = (contentTrackSectionId, contentTrackStepId) => {
      let progress = enrollment?.progress || {};
      if (progress && contentTrackSectionId) {
        progress = (progress.sectionStatuses || []).find(status => status.targetId == contentTrackSectionId);
      }
      if (progress && contentTrackStepId) {
        progress = (progress.stepStatuses || []).find(status_0 => status_0.targetId == contentTrackStepId);
      }
      return progress;
    };
    $[15] = enrollment?.progress;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const getProgress = t11;
  trackQuery.data?.contentTrack?.type;
  const t12 = trackQuery.data?.contentTrack;
  let t13;
  if ($[17] !== t12) {
    t13 = getTrackPathPrefix(t12);
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const pathPrefix = t13;
  const t14 = `${routeParams?.contentTrackSlug || ""}`;
  const t15 = `${routeParams?.contentTrackSectionSlug || ""}`;
  const t16 = `${routeParams?.contentTrackStepSlug || ""}`;
  const t17 = track?.progressionType;
  let t18;
  if ($[19] !== enrollment || $[20] !== getProgress || $[21] !== pathPrefix || $[22] !== t14 || $[23] !== t15 || $[24] !== t16 || $[25] !== t17 || $[26] !== track) {
    t18 = {
      contentTrackSlug: t14,
      contentTrackSectionSlug: t15,
      contentTrackStepSlug: t16,
      contentTrack: track,
      contentTrackEnrollment: enrollment,
      progressionType: t17,
      getProgress,
      pathPrefix
    };
    $[19] = enrollment;
    $[20] = getProgress;
    $[21] = pathPrefix;
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
    $[25] = t17;
    $[26] = track;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== props.children || $[29] !== t18) {
    t19 = <ContentTrackContext.Provider value={t18}>{props.children}</ContentTrackContext.Provider>;
    $[28] = props.children;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  return t19;
};
export function getTrackPathPrefix(contentTrack) {
  let value = 'courses';
  switch (contentTrack?.type) {
    case 'Sprint':
      value = 'sprints';
      break;
    default:
      value = 'courses';
      break;
  }
  return value;
}